import {
    DrawerPanelStyle,
    DrawerProps,
    Wrapper,
    DrawerSide,
    DrawerHeaderStyle,
    DrawerContentStyle
} from "./drawer.style";
import React, {forwardRef, useCallback, useEffect, useMemo, useRef} from "react";
import ReactDOM from "react-dom";
import {AnimatePresence, motion} from "motion/react";
import {circInOut, Variants} from "motion";
import {IconButtonComponent} from "../button/iconButton.component";
import {useLockBodyScroll} from "../../utils/common.utils";
import {twMerge} from "tailwind-merge"

const variantsBySide: Record<DrawerSide, { initial: any; animate: any; exit: any }> = {
    left: {initial: {x: "-100%"}, animate: {x: 0}, exit: {x: "-100%"}},
    right: {initial: {x: "100%"}, animate: {x: 0}, exit: {x: "100%"}},
    top: {initial: {y: "-100%"}, animate: {y: 0}, exit: {y: "-100%"}},
    bottom: {initial: {y: "100%"}, animate: {y: 0}, exit: {y: "100%"}},
};


export const DrawerComponent = forwardRef(({
                                               open,
                                               onClose,
                                               ariaLabel = "Drawer",
                                               lockScroll = true,
                                               closeOnEscape,
                                               onOpenChange,
                                               overlay,
                                               overlayClassName,
                                               closeOnOutside,
                                               header,
                                               headerClassName,
                                               showCloseButton,
                                               $backdrop = true,
                                               $side = "right",
                                               $size = "md",
                                               $backgroundColor,
                                               $fitScreenOnSmall,
                                               backBtnDisplay,
                                               children,
                                               ...props
                                           }: DrawerProps, ref) => {
    const panelRef = useRef<HTMLDivElement | null>(null);
    const lastFocusedRef = useRef<HTMLElement | null>(null);
    const handleOpenChange = useCallback((state: boolean) => {
        onOpenChange(state);
        if (!state && onClose) {
            onClose();
        }
    }, [onOpenChange, onClose]);

    useEffect(() => {
        if (open) {
            lastFocusedRef.current = document.activeElement as HTMLElement;
            const t = setTimeout(() => {
                panelRef.current?.focus();
            }, 1);
            return () => clearTimeout(t);
        } else if (lastFocusedRef.current) {
            lastFocusedRef.current.focus?.();
        }
    }, [open]);

    useEffect(() => {
        if (!open || !closeOnEscape) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault()
                handleOpenChange(false)
            }
        }

        window.addEventListener("keydown", onKey)
        return (() => window.removeEventListener("keydown", onKey))
    }, [open, closeOnEscape, onOpenChange, handleOpenChange])
    useLockBodyScroll(open && lockScroll);
    const handleOverlayClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (!closeOnOutside) return
        if (e.target === e.currentTarget) handleOpenChange(false)
    }, [closeOnOutside, handleOpenChange])

    const portalTarget = useMemo(() => (typeof window !== "undefined" ? document.body : null), [])
    if (!portalTarget) return null
    return ReactDOM.createPortal(<AnimatePresence>
        {open && <Wrapper>
            {overlay && <motion.div
                aria-hidden
                onMouseDown={handleOverlayClick}
                className={twMerge("backdrop-overlay", overlayClassName)}
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}
                transition={{
                    duration: 0.8,
                    when: "beforeChildren"
                }}
            />}
            <DrawerPanelStyle
                $backgroundColor={$backgroundColor}
                $fitScreenOnSmall={$fitScreenOnSmall}
                role={"dialog"}
                aria-model={"true"}
                aria-label={ariaLabel}
                ref={panelRef}
                tabIndex={-1}
                $backdrop={$backdrop}
                $side={$side}
                $size={$size}
                {...props}
                initial={variantsBySide[$side].initial}
                animate={{
                    ...variantsBySide[$side].animate,
                    transition: {type: "spring", stiffness: 260, damping: 30}
                }}
                exit={{...variantsBySide[$side].exit, transition: {duration: 0.2}}}>
                {(header || showCloseButton || backBtnDisplay) && (
                    <DrawerHeaderStyle className={twMerge("drawer-header", headerClassName)} $side={$side}>
                        {backBtnDisplay && (
                            <IconButtonComponent
                                variant={"ghost"}
                                className={"back-btn !shadow-none"}
                                onClick={() => handleOpenChange(false)}
                            >
                                <svg fill="currentColor" viewBox="0 0 96 96" xmlns="http://www.w3.org/2000/svg">
                                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                    <g id="SVGRepo_iconCarrier"><title></title>
                                        <path
                                            d="M39.3756,48.0022l30.47-25.39a6.0035,6.0035,0,0,0-7.6878-9.223L26.1563,43.3906a6.0092,6.0092,0,0,0,0,9.2231L62.1578,82.615a6.0035,6.0035,0,0,0,7.6878-9.2231Z"></path>
                                    </g>
                                </svg>
                            </IconButtonComponent>
                        )}
                        <div className={"header"}>
                            {header}
                        </div>
                        {showCloseButton && (
                            <IconButtonComponent
                                className={"close-btn"}
                                variant={"outline"}
                                onClick={() => handleOpenChange(false)}
                            >
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M1.87571 0.696715C1.55028 0.371278 1.02264 0.371277 0.697203 0.696715C0.371766 1.02215 0.371766 1.54979 0.697203 1.87523L4.82199 6.00002L0.697203 10.1248C0.371766 10.4502 0.371766 10.9779 0.697203 11.3033C1.02264 11.6288 1.55028 11.6288 1.87571 11.3033L6.0005 7.17853L10.1253 11.3033C10.4507 11.6288 10.9784 11.6288 11.3038 11.3033C11.6292 10.9779 11.6292 10.4502 11.3038 10.1248L7.17902 6.00002L11.3038 1.87523C11.6292 1.54979 11.6292 1.02215 11.3038 0.696715C10.9784 0.371278 10.4507 0.371278 10.1253 0.696715L6.0005 4.8215L1.87571 0.696715Z"
                                        fill="currentColor"/>
                                </svg>
                            </IconButtonComponent>
                        )}
                    </DrawerHeaderStyle>
                )}
                <DrawerContentStyle className={"drawer-content"}>
                    {children}
                </DrawerContentStyle>

            </DrawerPanelStyle>
        </Wrapper>}
    </AnimatePresence>, portalTarget)
})

export function useDrawerState(initial = false) {
    const [open, setOpen] = React.useState(initial);
    const onOpenChange = React.useCallback((v: boolean) => setOpen(v), []);
    return {open, setOpen, onOpenChange} as const;
}

export const drawerContentMotionVariants: Variants = {
    hidden: {
        opacity: 0.5,
        x: -50,
    },
    visible: {
        opacity: 1,
        x: 0,
        transition: {
            duration: 0.3,
            ease: circInOut,
            when: "beforeChildren",
            staggerChildren: 0.2,
        },
    },
};

DrawerComponent.displayName = "DrawerComponent"